import React, { useEffect, useState, useRef } from 'react';
import { useSettings } from '../context/SettingsContext';

const FALLBACK_LOGO = '';

// All the features we've implemented across this build — these stream down as Matrix text.
const FEATURES = [
  'Live Order Tracking',
  'Driver Dispatch',
  'Auto-Assign Nearest',
  'Web Push Notifications',
  'WebAuthn Passkeys',
  'Passkey Auto-Login',
  'B2B Trade Inquiries',
  'TV Slideshow',
  'Full-Width Ticker',
  'Featured Slides CMS',
  'Custom Category Filters',
  'GPS Forced at Checkout',
  'PDF Tax Invoices',
  'Brevo SMTP Emails',
  'Status-Aware Emails',
  'Reorder Previous Order',
  'PWA Install Prompt',
  'Service Worker Cache',
  'Driver Self-Signup',
  'Admin Approval Flow',
  'Driver Credentials Email',
  'Live Leaflet Maps',
  'Driver GPS Ping 20s',
  'Driver Status Flow',
  'Rate Limiting',
  'Bcrypt Password Hashing',
  'Timing-Safe Auth',
  'Customer Lock/Unlock',
  'Passkey Revoke',
  'In-App Notifications Bell',
  'Per-Order Chat',
  'Admin Broadcast',
  'WhatsApp Deep Links',
  'Click-to-Call',
  'Custom Product Attributes',
  'Homepage Visibility Toggles',
  'Dynamic Branding',
  'Pending Driver Banner',
  'Custom Slideshow Transitions',
  'Public Order Tracker',
  'Status Timeline',
  'Driver Welcome Email',
  'CORS Lockdown',
  'Sitemap PDF Export',
  'OTP via Email',
  'Forced GPS Capture',
];

const COL_WIDTH = 170;      // wider columns since we only show feature labels
const ROW_HEIGHT = 22;      // px per row
const FRAME_INTERVAL = 75;  // ms — lower = faster rain

const FONT = '700 13px "JetBrains Mono", "Fira Code", "SF Mono", Menlo, monospace';

const SplashScreen = ({ onDone }) => {
  const { settings } = useSettings();
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const setSize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    setSize();

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const cols = Math.ceil(window.innerWidth / COL_WIDTH);
    // Each column drops a single feature label, then recycles to a new feature
    const heads = Array.from({ length: cols }, () => Math.floor(Math.random() * -40));
    const featureForCol = Array.from({ length: cols }, () => FEATURES[Math.floor(Math.random() * FEATURES.length)]);

    const draw = () => {
      // Trail effect on white canvas — translucent white fade
      ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      ctx.font = FONT;
      ctx.textBaseline = 'top';

      for (let c = 0; c < cols; c += 1) {
        const x = c * COL_WIDTH + 4;
        const y = heads[c] * ROW_HEIGHT;
        const label = featureForCol[c];

        // Bright head — primary brand blue
        ctx.fillStyle = '#0078D4';
        ctx.fillText(label, x, y);
        // Trail (slightly lighter, one row up)
        ctx.fillStyle = 'rgba(0, 120, 212, 0.4)';
        ctx.fillText(label, x, y - ROW_HEIGHT);
        // Faint older trail
        if (heads[c] > 2) {
          ctx.fillStyle = 'rgba(0, 120, 212, 0.18)';
          ctx.fillText(label, x, y - ROW_HEIGHT * 2);
        }

        heads[c] += 1;
        // Recycle when off-screen (with random delay) and pick a new feature
        if (heads[c] * ROW_HEIGHT > window.innerHeight && Math.random() > 0.95) {
          heads[c] = Math.floor(Math.random() * -20);
          featureForCol[c] = FEATURES[Math.floor(Math.random() * FEATURES.length)];
        }
      }
    };

    const id = setInterval(draw, FRAME_INTERVAL);
    animRef.current = id;
    const onResize = () => setSize();
    window.addEventListener('resize', onResize);

    // Fade out the whole splash after 3.2s, then call onDone
    const fadeT = setTimeout(() => setFading(true), 3200);
    const doneT = setTimeout(() => {
      clearInterval(id);
      onDone && onDone();
    }, 4000);

    return () => {
      clearInterval(id);
      clearTimeout(fadeT);
      clearTimeout(doneT);
      window.removeEventListener('resize', onResize);
    };
  }, [onDone]);

  const logo = settings?.logo_image || FALLBACK_LOGO;

  return (
    <div
      className={`fixed inset-0 z-[10000] bg-white transition-opacity duration-700 ${
        fading ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      data-testid="splash-screen"
      aria-hidden={fading}
    >
      <canvas ref={canvasRef} className="absolute inset-0 block" />

      {/* Vignette to focus eyes on the centre — light wash */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 60%, rgba(255,255,255,0.95) 100%)' }} />

      {/* Centred logo + tagline */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="splash-logo">
          <img
            src={logo}
            alt="Mohammed Riaz & Partner LLC"
            className="w-44 h-44 sm:w-56 sm:h-56 md:w-64 md:h-64 object-contain drop-shadow-2xl"
            draggable="false"
          />
        </div>
        <div className="mt-6 font-display text-[#0A2A4E] text-2xl sm:text-3xl font-bold tracking-tight splash-fade">
          Mohammed Riaz & Partner LLC
        </div>
        <div className="mt-1.5 text-[11px] uppercase tracking-[0.4em] font-bold text-[#0078D4]/80 splash-fade-delay">
          B2B Wholesale · Muscat
        </div>
      </div>

      <style>{`
        .splash-logo {
          animation: splash-pulse 1.8s ease-in-out infinite, splash-pop 0.5s ease-out;
          border-radius: 16px;
        }
        .splash-fade { animation: splash-fade-in 0.7s ease-out 0.25s both; }
        .splash-fade-delay { animation: splash-fade-in 0.7s ease-out 0.6s both; }
        @keyframes splash-pop {
          from { opacity: 0; transform: scale(0.7); filter: blur(8px); }
          to   { opacity: 1; transform: scale(1);   filter: blur(0); }
        }
        @keyframes splash-pulse {
          0%, 100% { filter: drop-shadow(0 0 0 rgba(0,120,212,0.0)); }
          50%      { filter: drop-shadow(0 0 24px rgba(0,120,212,0.45)); }
        }
        @keyframes splash-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
        @media (prefers-reduced-motion: reduce) {
          .splash-logo, .splash-fade, .splash-fade-delay { animation-duration: 0.2s !important; }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
